/**
 * Admin · Users — list/search signed-up users with assessment counts + admin
 * badge, and delete a user (cascades their assessments + related records).
 * Admin status is governed by ADMIN_EMAILS on the server; admins can't be
 * deleted from here (the API blocks it).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, Search, Trash2, ShieldCheck, Users as UsersIcon } from "lucide-react";

export default function UsersPanel() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async (search = "") => {
    try {
      const { data } = await api.get("/admin/users", { params: search ? { q: search } : {} });
      setUsers(data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
      setUsers([]);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This removes their account and all ${u.assessment_count} assessment(s) and related data. This cannot be undone.`)) return;
    setBusyId(u.user_id);
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User deleted.");
      load(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  if (users === null) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  return (
    <div className="space-y-5" data-testid="users-panel">
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900 p-4 flex items-start gap-3">
        <ShieldCheck className="h-5 w-5 text-teal-700 dark:text-teal-400 mt-0.5 shrink-0" />
        <p className="text-sm text-slate-700 dark:text-slate-200">
          Admin access is controlled by the <code className="font-mono text-xs">ADMIN_EMAILS</code> setting on the server.
          Admins are badged below and can't be deleted here — to remove an admin, take their email out of that list first.
        </p>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); load(q); }}
        className="flex items-center gap-2"
      >
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or email…"
            data-testid="users-search"
            className="w-full h-10 pl-9 pr-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm text-slate-900 dark:text-slate-100"
          />
        </div>
        <button type="submit" data-testid="users-search-btn" className="h-10 px-4 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors">
          Search
        </button>
      </form>

      <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
        <UsersIcon className="h-4 w-4" /> {users.length} user{users.length === 1 ? "" : "s"}
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900">
              <th className="px-4 py-3 font-medium">User</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Joined</th>
              <th className="px-4 py-3 font-medium text-center">Assessments</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="text-slate-700 dark:text-slate-200">
            {users.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500 dark:text-slate-400 italic">No users found.</td></tr>
            ) : users.map((u) => (
              <tr key={u.user_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`user-row-${u.user_id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 dark:text-slate-50">{u.name || "—"}</span>
                    {u.is_admin && (
                      <span className="px-2 py-0.5 rounded-full bg-teal-100 dark:bg-teal-500/15 text-teal-800 dark:text-teal-300 text-[10px] font-medium uppercase tracking-wide">Admin</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{u.email}</td>
                <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—"}
                </td>
                <td className="px-4 py-3 text-center">{u.assessment_count}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => remove(u)}
                    disabled={u.is_admin || busyId === u.user_id}
                    title={u.is_admin ? "Admins can't be deleted here" : "Delete user"}
                    data-testid={`user-delete-${u.user_id}`}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-red-200 dark:border-red-500/40 text-red-700 dark:text-red-300 text-xs font-medium hover:bg-red-50 dark:hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {busyId === u.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
